// Typed access to biology-config.json. Every tunable constant lives in that
// file so verified husbandry values from ../clay-farm-science can replace the
// current estimates without touching code (BIOLOGY-ENGINE.md §4).

import raw from '../data/biology-config.json';
import type { Breed } from '../core/types';

export type Profile = 'normal' | 'veteran';

export interface BreedProfile {
  docility: number;
  flightiness: number;
  broodiness: number;
  waterproof: boolean;
  toes: number;
  eggRatePerWeek: number;
  eggsPerYear: number;
  feedConsumptionDailyKg: number;
  difficultyMultiplier: number;
  onsetWeeks: number;
}

type NormalProfile = (typeof raw)['profiles']['normal'];
/** Veteran only overrides some leaves, so its shape is a deep partial. */
export type BiologyConfig = NormalProfile & {
  grace: NormalProfile['grace'] & { criticalGraceHours?: number };
};

type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K] };

function deepMerge<T>(base: T, override: DeepPartial<T>): T {
  const out = { ...base } as T;
  for (const key of Object.keys(override) as (keyof T)[]) {
    const value = override[key];
    if (value === undefined) continue;
    const current = out[key];
    out[key] =
      current && typeof current === 'object' && !Array.isArray(current)
        ? deepMerge(current, value as DeepPartial<T[keyof T]>)
        : (value as T[keyof T]);
  }
  return out;
}

const NORMAL = raw.profiles.normal as BiologyConfig;
const VETERAN = deepMerge(
  NORMAL,
  raw.profiles.veteran as unknown as DeepPartial<BiologyConfig>,
);

/** Live overrides from the dev dashboard sliders; cleared on reload. */
let overrides: DeepPartial<BiologyConfig> = {};

export function getConfig(profile: Profile = 'normal'): BiologyConfig {
  const base = profile === 'veteran' ? VETERAN : NORMAL;
  return Object.keys(overrides).length ? deepMerge(base, overrides) : base;
}

export function setConfigOverrides(next: DeepPartial<BiologyConfig>): void {
  overrides = next;
}

export function clearConfigOverrides(): void {
  overrides = {};
}

export const BREEDS = raw.breeds as Record<Breed, BreedProfile>;
export const LIFE_STAGES = raw.lifeStages;

/**
 * Every breed the config carries a row for, in config order. The `breeds` object
 * also holds the file's own `_source` note, so the underscore keys are dropped
 * rather than every caller remembering to.
 */
export const BREED_IDS = Object.keys(BREEDS).filter((key) => !key.startsWith('_')) as Breed[];

export function breedProfile(breed: Breed): BreedProfile {
  return BREEDS[breed];
}
